import React from 'react';
import { CircularProgress, Box, Typography } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { useMessage } from 'modules/intl';

type CircularProgressWithLabelProps = {
  progress: number;
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: theme.spacing(32),
    width: theme.spacing(56),
    background: '#fff',
    color: '#000',
    padding: theme.spacing(6, 10),
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  heading: {
    color: theme.palette.text.primary
  },
  subHeading: {
    color: theme.palette.text.secondary
  },
  progress: {
    marginTop: theme.spacing(2)
  },
  box: {
    top: theme.spacing(8),
    left: 0,
    bottom: 0,
    right: 0,
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary
  },
  done: {
    margin: theme.spacing(0),
    color: theme.palette.success.main,
    fontSize: theme.spacing(15)
  }
}));

export const CircularProgressWithLabel: React.FC<CircularProgressWithLabelProps> = ({
  progress
}: CircularProgressWithLabelProps) => {
  const classes = useStyles();
  const loadingMsg = useMessage('smartDiary.loading');

  return (
    <Box className={classes.root}>
      {progress === 100 ? (
        <>
          <Typography
            variant="h6"
            className={classes.subHeading}
            component="div"
          >
            <FormattedMessage id="smartDiary.submitProgress.done" />
          </Typography>
          <CheckCircle
            data-testid="circular-completed"
            className={classes.done}
          />
        </>
      ) : (
        <>
          <Typography variant="h6" className={classes.heading} component="div">
            <FormattedMessage id="smartDiary.submitProgress.heading" />
          </Typography>
          <Typography
            variant="body2"
            className={classes.subHeading}
            component="div"
          >
            <FormattedMessage id="smartDiary.submitProgress.subHeading" />
          </Typography>
          <CircularProgress
            variant="determinate"
            data-testid="circular-progress"
            value={progress}
            size={100}
            thickness={2}
            className={classes.progress}
            aria-label={loadingMsg}
          ></CircularProgress>
          <Box className={classes.box}>
            <Typography variant="h6">{`${Math.round(progress)}%`}</Typography>
          </Box>
        </>
      )}
    </Box>
  );
};
